"use client";
import { useState, useEffect } from "react";
import Link from "next/link";
import { Button } from "@ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@ui/sheet";
import { Menu, X, Leaf } from "lucide-react";
import { Logo, LogoMobile, BlackLogo, BlackLogoMobile } from "@icons/svgs";
import { APP_SIGNUP_URL } from "@/client/components/shared";

export const LandingStickyNav = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    setIsOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const navLinks = [
    { label: "Programs", id: "programs" },
    { label: "Eligibility", id: "eligibility" },
    { label: "Why OICNM", id: "trust" },
    { label: "Admissions", id: "admissions" },
    { label: "Video Gallery", id: "videos" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 font-sans-body ${
        scrolled
          ? "bg-background/95 backdrop-blur-md shadow-md border-b border-border"
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          {!scrolled ? (
            <>
              <div className="hidden md:block">
                <Link className="cursor-pointer" href="/">
                  <Logo />
                </Link>
              </div>
              <div className="md:hidden">
                <Link className="cursor-pointer" href="/">
                  <LogoMobile />
                </Link>
              </div>
            </>
          ) : (
            <>
              {" "}
              <div className="hidden md:block">
                <Link className="cursor-pointer" href="/">
                  <BlackLogo />
                </Link>
              </div>
              <div className="md:hidden">
                <Link className="cursor-pointer" href="/">
                  <BlackLogoMobile />
                </Link>
              </div>
            </>
          )}

          {/* Desktop links */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors bg-transparent border-none ${
                  scrolled
                    ? "text-muted-foreground hover:text-foreground hover:bg-muted"
                    : "text-white/80 hover:text-white hover:bg-white/10"
                }`}
              >
                {link.label}
              </button>
            ))}
            <Link
              href={APP_SIGNUP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-3"
            >
              <Button
                size="sm"
                className="bg-wisdom-500 hover:bg-wisdom-600 text-white font-semibold border-none"
              >
                Apply Now
              </Button>
            </Link>
          </div>

          {/* Mobile */}
          <div className="flex items-center gap-2 lg:hidden">
            <Link href={APP_SIGNUP_URL} target="_blank" rel="noopener noreferrer">
              <Button
                size="sm"
                className="bg-wisdom-500 hover:bg-wisdom-600 text-white font-semibold text-xs animate-pulse-glow border-none"
              >
                Apply Now
              </Button>
            </Link>
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={
                    scrolled ? "" : "text-white bg-transparent border-none"
                  }
                >
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px]">
                <div className="hidden md:block">
                  <BlackLogo />
                </div>
                <div className="md:hidden">
                  <BlackLogoMobile />
                </div>

                <div className="flex flex-col gap-1 mt-10">
                  {navLinks.map((link) => (
                    <button
                      key={link.id}
                      onClick={() => scrollToSection(link.id)}
                      className="text-left px-4 py-3 text-sm font-medium text-foreground hover:bg-muted rounded-md transition-colors"
                    >
                      {link.label}
                    </button>
                  ))}
                  <Link
                    href={APP_SIGNUP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4"
                    onClick={() => setIsOpen(false)}
                  >
                    <Button variant="outline" size="sm" className="w-full">
                      Apply here
                    </Button>
                  </Link>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};
